<template>
  <RevInfoBlock class="bg-static-info-low" :icon :title variant="info">
    <RevTag
      v-if="backRepairTagLabel"
      :label="backRepairTagLabel"
      variant="info"
    />

    <CustomerIssues
      v-if="claim.customerIssues.length"
      :customer-issues="claim.customerIssues"
    />

    <LabelReturn v-if="claim.labelReturn" :label-return="claim.labelReturn" />
  </RevInfoBlock>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { RevTag } from '@ds/components/Tag'
import { IconExport } from '@ds/icons/IconExport'
import { IconHeartDiagnostic } from '@ds/icons/IconHeartDiagnostic'
import { IconRefund } from '@ds/icons/IconRefund'
import { IconTools } from '@ds/icons/IconTools'

import type { Claim } from '~/api/customer-request/types'

import CustomerIssues from '../CustomerIssues/CustomerIssues.vue'
import LabelReturn from '../LabelReturn/LabelReturn.vue'

import translations from './InWarrantyInfoBlock.translations'

const i18n = useI18n()

const props = defineProps<{ claim: Claim }>()

const icon = computed(() => {
  const iconsUsingResolutionType = {
    FACTORY_REPAIR: IconTools,
    SHOP_REPAIR: IconTools,
    REPAIR_REPLACE: IconTools,
    REMOTE_ASSISTANCE: IconHeartDiagnostic,
    REFUND: IconRefund,
    SELLER_REPAIR: IconTools,
    SELLER_REPLACE: IconExport,
    BACKREPLACE: IconExport,
  }

  // Legacy: Waiting for the API to be updated
  const iconsUsingResolutionOption = {
    LIVE_CHAT: IconHeartDiagnostic,
    REPAIR_REPLACE: IconTools,
    REPAIR: IconTools,
    REMOTE_ASSISTANCE: IconHeartDiagnostic,
    REFUND: IconRefund,
    REPLACE: IconExport,
  }

  if (!props.claim.resolutionType || !props.claim.resolutionOption) {
    return IconHeartDiagnostic
  }

  return (
    iconsUsingResolutionType[props.claim.resolutionType] ||
    iconsUsingResolutionOption[props.claim.resolutionOption]
  )
})

const title = computed(() =>
  i18n(translations.title, {
    resolution: props.claim.resolutionType || props.claim.resolutionOption,
    count: props.claim.customerIssues.length,
    source: props.claim.source,
  }),
)

const backRepairTagLabel = computed(() => {
  if (props.claim.resolutionType === 'FACTORY_REPAIR') {
    return `${i18n(translations.backRepair)} • ${i18n(translations.mailIn)}`
  }

  if (props.claim.resolutionType === 'SHOP_REPAIR') {
    return `${i18n(translations.backRepair)} • ${i18n(translations.inShop)}`
  }

  return null
})
</script>
